// ModuleCelebration.tsx — a warm, up-front moment when a chapter is finished.
// Shown full-screen the instant the last step of a module completes. At the end
// of a MAIN (core) chapter it also carries the "how are you managing?" check-in
// as a tile; intro/summary chapters celebrate without it.
import { Btn, Display } from '@/components/ui';
import { Icon } from '@/components/icons';
import { ChapterCheckInTile } from './ChapterCheckInTile';
import { GuidePresence } from '@/flows/guide/GuidePresence';
import { ReviewCommentButton } from '@/flows/review/ReviewCommentButton';
import type { GuideSelection } from '@/services/guidePresence';
import type { Module } from '@/types';
import { moduleName } from '@/services/contentProvider';
import { GUIDE_MESSAGES } from '@/data/guideMessages';
import { useIsDesktop } from '@/hooks/useIsDesktop';

// Founder quotes that celebrate a chapter end (Batch B #4). Rotated by chapter
// order so each chapter shows a settled, non-repeating line from Craig's pools.
const CHAPTER_QUOTES = [
  ...(GUIDE_MESSAGES.H5_mastery.quotePool ?? []),
  ...(GUIDE_MESSAGES.H1_welcome.quotePool ?? []),
];

export function ModuleCelebration({
  module,
  chapterLabel,
  nextModuleTitle,
  checkIn,
  guide,
  onGuideShown,
  onContinue,
}: {
  module: Module;
  /** Act-named locator, e.g. "Feel in control · Chapter 3". Names the act so the
   *  per-act chapter number reads sensibly deep in the programme. */
  chapterLabel?: string;
  /** Title of the chapter the Continue button leads into, if any. */
  nextModuleTitle?: string;
  /** When set, show the "how are you managing?" tile (core chapters only). */
  checkIn?: { onCalmYourBody?: () => void; onSelfCompassion?: () => void };
  /** H8 milestone — an occasional "well done" guide face (no quote). */
  guide?: GuideSelection | null;
  /** Fired once when the guide face is shown, so the impression is committed. */
  onGuideShown?: () => void;
  onContinue: () => void;
}) {
  const isDesktop = useIsDesktop();
  return (
    <div
      style={{
        // fixed + high z-index so the chapter-complete celebration is a TRUE
        // full-screen takeover — the cream fills the WHOLE viewport (covering the
        // header, nav rail and the feed behind it on web). The content itself is
        // re-centred in a reading column below; earlier this layer was width-capped,
        // which left the feed peeking down one side on a wide desktop.
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'var(--cream)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'rise .5s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      {/* Review Mode: a chapter-level comment affordance. */}
      <ReviewCommentButton targetId={module.id} kind="chapter" label={moduleName(module)} />

      {/* gold light from above */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 360,
          background:
            'radial-gradient(120% 90% at 50% -12%, rgba(246,212,154,0.55), rgba(232,176,84,0.16) 45%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      {/* Content column — re-centred and width-capped for readability, while the
          cream backdrop above stays full-bleed. */}
      <div
        style={{
          width: '100%',
          maxWidth: isDesktop ? 760 : 520,
          marginLeft: 'auto',
          marginRight: 'auto',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: checkIn ? 'flex-start' : 'center',
          textAlign: 'center',
          padding: 'calc(var(--safe-top) + 32px) 26px 0',
          position: 'relative',
        }}
      >
        {/* medallion */}
        <div
          style={{
            width: 92,
            height: 92,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#FBF1DE',
            background: 'linear-gradient(150deg, #E1A943, #9B6712)',
            boxShadow: '0 1px 0 rgba(246,212,154,0.4) inset, 0 22px 44px -16px rgba(150,96,22,0.6)',
            marginBottom: 22,
          }}
        >
          <Icon name="check" size={42} sw={2.4} />
        </div>

        <div
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--amber-deep)',
            marginBottom: 12,
          }}
        >
          {chapterLabel ? `${chapterLabel} complete` : 'Chapter complete'}
        </div>

        <Display parts={{ pre: moduleName(module), em: '' }} size={28} lh={1.18} />

        {/* The italic chapter quote and Craig's milestone face say the same thing
            ("you finished — let it land"), so they must never stack. On the Nth-chapter
            milestone the guide FACE owns the moment; otherwise the quote carries it. */}
        {!guide && (
          <p
            style={{
              margin: '16px 0 0',
              maxWidth: 330,
              fontFamily: 'Fraunces, Georgia, serif',
              fontStyle: 'italic',
              fontWeight: 300,
              fontSize: 18,
              lineHeight: 1.55,
              color: 'var(--amber-deep)',
            }}
          >
            {CHAPTER_QUOTES.length
              ? `“${CHAPTER_QUOTES[(module.order ?? 1) % CHAPTER_QUOTES.length]?.text}”`
              : 'You moved through every step here. That takes courage — let it land for a moment before you carry on.'}
          </p>
        )}

        {guide && (
          <div style={{ width: '100%', maxWidth: 380, marginTop: 24 }}>
            <GuidePresence selection={guide} plain onShown={onGuideShown} />
          </div>
        )}

        {checkIn && (
          <div style={{ width: '100%', maxWidth: 380, marginTop: 24 }}>
            <ChapterCheckInTile onCalmYourBody={checkIn.onCalmYourBody} onSelfCompassion={checkIn.onSelfCompassion} />
          </div>
        )}
      </div>

      <div
        style={{
          padding: '16px 26px calc(26px + var(--safe-bottom))',
          background: 'linear-gradient(180deg, transparent, var(--cream) 30%)',
        }}
      >
        <Btn full size="lg" onClick={onContinue} leading={<Icon name="journey" size={18} />}>
          {nextModuleTitle ? 'Continue to next chapter' : 'Continue'}
        </Btn>
      </div>
      </div>
    </div>
  );
}
